/**
 * The client half of Crewlink's speech-to-text boundary. M2 / S-02B-ii.
 *
 *   RecorderPort → temporary audio Blob → THIS MODULE → Crewlink Edge Function
 *
 * WHAT IS NOT IN THIS FILE, AND MUST NEVER BE (D-012):
 *
 *   No provider. No provider NAME. No API key. No provider endpoint. The client
 *   posts audio to Crewlink's own authenticated endpoint and receives a
 *   transcript. **Which transcription service is behind that endpoint is a
 *   server-side fact the client is not told and does not need**, so a change of
 *   provider changes nothing here.
 *
 *   The vendor is deliberately not named even in a COMMENT. That is not
 *   superstition: it lets `npm run check:secrets:build` assert the strong,
 *   checkable property — the name appears in no client source and in no built
 *   artifact — instead of the weak one, "no key appears". A guarantee that has
 *   to carve out exceptions for prose is a guarantee nobody can verify.
 *
 * WHY THIS SITS IN `src/data/` RATHER THAN `src/platform/`:
 *
 *   `src/platform/` is the DEVICE-capability layer — a web implementation and a
 *   Capacitor implementation of the same native capability. A call to
 *   Crewlink's server is neither; it is identical on both platforms. D-012 is
 *   explicit that `SttPort` must not live in `src/platform/`, and this module
 *   is not `SttPort` — that lives on the server. This is a client of it, and it
 *   belongs beside the other modules that talk to Crewlink's backend.
 *
 * The layering rule is not bypassed: this issues no table mutation. It is one
 * authenticated HTTP call to a Crewlink endpoint, and it writes nothing.
 */

import { getSupabaseClient } from './supabase-client';
import { readSupabaseEnv } from '../lib/env';

/** The Edge Function's name. One place, so the path cannot drift. */
export const STT_FUNCTION_NAME = 'stt-transcribe';

/** The multipart field name, matching the server's `AUDIO_FIELD`. */
export const STT_AUDIO_FIELD = 'file';

/**
 * The outcome of a transcription attempt.
 *
 * Exactly one of the two is non-null. A separate `error` string rather than a
 * thrown exception because every caller has to render the failure — an
 * unhandled rejection in a UI is a blank panel and a console message nobody
 * sees (PT §20).
 */
export interface TranscriptionOutcome {
  readonly transcript: string | null;
  readonly error: string | null;
}

export interface TranscriptionRequest {
  readonly endpointUrl: string;
  readonly accessToken: string;
  readonly anonKey: string;
  readonly audio: Blob;
  readonly fetchImpl?: typeof fetch;
}

/**
 * Turns a server response into something Brandon can act on.
 *
 * The server's `code` is a stable contract; its `message` is already
 * user-facing English. The message is preferred when present, and these are the
 * fallbacks for a response that arrives without one.
 */
function describeFailure(status: number, code: string | null): string {
  if (status === 401) return 'Sign in to Crewlink to transcribe this recording.';
  if (code === 'audio_too_large') return 'That recording is too long to transcribe.';
  if (code === 'unsupported_audio_type') return 'That audio format cannot be transcribed.';
  if (code === 'empty_audio' || code === 'missing_audio') return 'There was no audio to send.';
  if (status >= 500) return 'Transcription did not complete. Try again.';
  return 'The recording could not be transcribed.';
}

/**
 * Posts audio to a Crewlink transcription endpoint.
 *
 * Every input is a parameter, including `fetch`, so the whole contract —
 * success, each controlled failure, a network drop, a body that is not JSON —
 * is exercisable without a server. Session resolution is the caller's job and
 * lives in `transcribeRecording` below.
 */
export async function requestTranscript(
  request: TranscriptionRequest,
): Promise<TranscriptionOutcome> {
  const doFetch = request.fetchImpl ?? fetch;

  const form = new FormData();
  form.append(STT_AUDIO_FIELD, request.audio, 'recording');

  let response: Response;
  try {
    response = await doFetch(request.endpointUrl, {
      method: 'POST',
      headers: {
        // Content-Type is left unset on purpose — `fetch` derives it from the
        // FormData together with the multipart boundary. Setting it by hand
        // drops the boundary and the server cannot parse the body.
        Authorization: `Bearer ${request.accessToken}`,
        apikey: request.anonKey,
      },
      body: form,
    });
  } catch {
    return {
      transcript: null,
      error: 'Could not reach Crewlink. Check your connection and try again.',
    };
  }

  let payload: unknown;
  try {
    payload = (await response.json()) as unknown;
  } catch {
    // A body that is not JSON is a real case — a gateway 502 is usually HTML —
    // and it must fall through to the status-based wording below, not throw.
    payload = null;
  }

  if (!response.ok) {
    const error = (payload as { error?: { code?: unknown; message?: unknown } } | null)?.error;
    const code = typeof error?.code === 'string' ? error.code : null;
    const message = typeof error?.message === 'string' ? error.message : null;
    return { transcript: null, error: message ?? describeFailure(response.status, code) };
  }

  const transcript = (payload as { transcript?: unknown } | null)?.transcript;
  if (typeof transcript !== 'string') {
    // A 200 that does not carry a transcript is a broken contract, and saying
    // so is more useful than rendering an empty panel as if it had worked.
    return { transcript: null, error: 'Crewlink returned no transcript.' };
  }

  return { transcript, error: null };
}

/**
 * Transcribes a recording using the signed-in Crewlink session.
 *
 * **Signed out is a refusal, and it happens here as well as at the server.**
 * The endpoint is the real boundary and rejects unauthenticated callers on its
 * own; this check exists so a signed-out user gets a sentence telling them what
 * to do instead of a 401 rendered as a generic failure. It is a courtesy, never
 * the protection — the server's refusal does not depend on it.
 *
 * **THIS FUNCTION IS TOTAL (I-026, fixed M3 / S-03-03).** It promises that
 * exactly one of `transcript` and `error` is non-null, and a promise like that
 * is only worth making if no code path can throw instead. The one path that
 * could — `client.auth.getSession()` rejecting — used to escape as an
 * exception, so a caller with a `finally` but no `catch` returned to an
 * idle-looking screen with no message at all. The rejection is now caught HERE,
 * once, rather than remembered at every call site. The caught error's own
 * message is deliberately not surfaced: a session-layer failure can quote
 * internals, and the fixed sentence tells the user the one thing they can do.
 */
export async function transcribeRecording(
  audio: Blob,
  fetchImpl?: typeof fetch,
): Promise<TranscriptionOutcome> {
  const env = readSupabaseEnv();
  const client = getSupabaseClient();
  if (!env || !client) {
    return { transcript: null, error: 'Crewlink is not connected to a server.' };
  }

  let accessToken: string | undefined;
  try {
    const { data } = await client.auth.getSession();
    accessToken = data.session?.access_token;
  } catch {
    return {
      transcript: null,
      error: 'Could not confirm your Crewlink sign-in. Try again.',
    };
  }
  if (!accessToken) {
    return { transcript: null, error: 'Sign in to Crewlink to transcribe this recording.' };
  }

  return await requestTranscript({
    endpointUrl: `${env.url.replace(/\/+$/, '')}/functions/v1/${STT_FUNCTION_NAME}`,
    accessToken,
    anonKey: env.anonKey,
    audio,
    ...(fetchImpl ? { fetchImpl } : {}),
  });
}
